import { type ReactElement } from "react";
import {
  ALL_STRATEGIES,
  type DateRangeKey,
  type Horizon,
  type TradeLogFilters,
} from "@/types/tradeLog";
import type { ExecutionMode, Strategy } from "@/types/strategy";

type ShowMode = "trades" | "alerts" | "both";

interface Props {
  filters: TradeLogFilters;
  onChange: (f: TradeLogFilters) => void;
  strategies: Strategy[];
  horizon: Horizon;
  onHorizonChange: (h: Horizon) => void;
}

export function FiltersBar({
  filters,
  onChange,
  strategies,
  horizon,
  onHorizonChange,
}: Readonly<Props>): ReactElement {
  return (
    <div className="mb-4 flex flex-wrap items-center gap-3">
      <RangeChips
        current={filters.range}
        onPick={(range) => {
          onChange({ ...filters, range });
        }}
      />
      <StrategySelect
        strategies={strategies}
        current={filters.strategyId}
        onPick={(strategyId) => {
          onChange({ ...filters, strategyId });
        }}
      />
      <HorizonToggle current={horizon} onPick={onHorizonChange} />
      <ExecutionModeToggle
        current={filters.executionMode ?? "LIVE"}
        onPick={(executionMode) => {
          onChange({ ...filters, executionMode });
        }}
      />
      <ShowToggle
        current={filters.show}
        onPick={(show) => {
          onChange({ ...filters, show });
        }}
      />
    </div>
  );
}

interface HorizonToggleProps {
  current: Horizon;
  onPick: (v: Horizon) => void;
}

/** Intraday vs Positional (operator, 2026-08-10). Different books: a day's
 * scalps and multi-day baskets share nothing but the word "trade", so the
 * table shows one horizon at a time. */
function HorizonToggle({ current, onPick }: Readonly<HorizonToggleProps>): ReactElement {
  return (
    <div className="flex rounded-ctl border border-line p-0.5 text-xs">
      {(["INTRADAY", "POSITIONAL"] as const).map((k) => {
        const active = k === current;
        const cls = active
          ? "rounded-ctl bg-accent-bg px-3 py-1 font-medium text-accent"
          : "rounded-ctl px-3 py-1 text-ink-secondary hover:bg-surface-2";
        return (
          <button
            key={k}
            type="button"
            className={cls}
            onClick={() => {
              onPick(k);
            }}
          >
            {k === "INTRADAY" ? "Intraday" : "Positional"}
          </button>
        );
      })}
    </div>
  );
}

interface RangeChipsProps {
  current: DateRangeKey;
  onPick: (r: DateRangeKey) => void;
}

const RANGES: { key: DateRangeKey; label: string }[] = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "week", label: "This week" },
  { key: "month", label: "This month" },
];

function RangeChips({ current, onPick }: Readonly<RangeChipsProps>): ReactElement {
  return (
    <div className="flex gap-1.5">
      {RANGES.map((r) => {
        const active = r.key === current;
        const cls = active
          ? "pill bg-accent-bg text-accent"
          : "pill border border-line-strong bg-transparent text-ink-secondary hover:bg-surface-2";
        return (
          <button
            key={r.key}
            type="button"
            className={cls}
            onClick={() => {
              onPick(r.key);
            }}
          >
            {r.label}
          </button>
        );
      })}
    </div>
  );
}

interface StrategySelectProps {
  strategies: Strategy[];
  current: string;
  onPick: (id: string) => void;
}

function StrategySelect({
  strategies,
  current,
  onPick,
}: Readonly<StrategySelectProps>): ReactElement {
  return (
    <label className="flex items-center gap-2 text-sm">
      <span className="text-ink-secondary">Strategy</span>
      <select
        className="rounded-ctl border border-line bg-surface-1 px-2 py-1 text-sm"
        value={current}
        onChange={(e) => {
          onPick(e.target.value);
        }}
      >
        <option value={ALL_STRATEGIES}>All</option>
        {strategies.map((s) => (
          <option key={s.id} value={s.id}>
            {s.name}
          </option>
        ))}
      </select>
    </label>
  );
}

interface ExecutionModeToggleProps {
  current: ExecutionMode;
  onPick: (v: ExecutionMode) => void;
}

/** Live vs Paper. Paper trades are kept fully separate from live numbers,
 * so this swaps the whole table between the two universes. */
function ExecutionModeToggle({
  current,
  onPick,
}: Readonly<ExecutionModeToggleProps>): ReactElement {
  return (
    <div className="flex rounded-ctl border border-line p-0.5 text-xs">
      {(["LIVE", "PAPER"] as const).map((k) => {
        const active = k === current;
        const activeCls =
          k === "PAPER" ? "bg-warning-bg text-warning" : "bg-success-bg text-success";
        const cls = active
          ? `rounded-ctl px-3 py-1 font-medium ${activeCls}`
          : "rounded-ctl px-3 py-1 text-ink-secondary hover:bg-surface-2";
        return (
          <button
            key={k}
            type="button"
            className={cls}
            onClick={() => {
              onPick(k);
            }}
          >
            {k === "LIVE" ? "Live" : "Paper"}
          </button>
        );
      })}
    </div>
  );
}

interface ShowToggleProps {
  current: ShowMode;
  onPick: (v: ShowMode) => void;
}

function ShowToggle({ current, onPick }: Readonly<ShowToggleProps>): ReactElement {
  return (
    <div className="ml-auto flex rounded-ctl border border-line p-0.5 text-xs">
      {(["trades", "alerts", "both"] as const).map((k) => {
        const active = k === current;
        const cls = active
          ? "rounded-ctl bg-accent-bg px-3 py-1 text-accent font-medium"
          : "rounded-ctl px-3 py-1 text-ink-secondary hover:bg-surface-2";
        return (
          <button
            key={k}
            type="button"
            className={cls}
            onClick={() => {
              onPick(k);
            }}
          >
            {labelFor(k)}
          </button>
        );
      })}
    </div>
  );
}

function labelFor(k: ShowMode): string {
  if (k === "trades") return "Trades";
  if (k === "alerts") return "Alerts";
  return "Both";
}
